/** @domain company -- Company profile fields: name, website, CUSIP, SIC code, stock exchange, features. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces, uppercaseOnly } from './formatters';

const define = createDomainField('company');

export const companyName = define({
  label: 'Company Name',
  placeholder: 'Enter company name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const websiteUrl = define({
  label: 'Website URL',
  placeholder: 'https://example.com',
  schema: z
    .string()
    .regex(
      /^https?:\/\/(?:localhost(?::[0-9]{1,5})?|(?:[\w.-]+(?:\.[\w.-]+)+))[\w\-._~:/?#[\]@!$&'()*+,;=.]*$/,
      'Must be a valid URL starting with http:// or https://',
    ),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const domainName = define({
  label: 'Domain Name',
  placeholder: 'example.com',
  schema: z
    .string()
    .regex(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, 'Must be a valid domain name'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const cusip = define({
  label: 'CUSIP',
  placeholder: 'Enter CUSIP',
  schema: z
    .string()
    .max(9, 'Must be at most 9 characters')
    .regex(/^[A-Z0-9]+$/, 'Must be alphanumeric uppercase'),
  adapterHint: 'text',
  formatter: uppercaseOnly,
  displayFormat: 'text',
});

export const sicCode = define({
  label: 'SIC Code',
  placeholder: 'Enter SIC code',
  schema: z.string().max(10, 'Must be at most 10 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const businessDescription = define({
  label: 'Business Description',
  placeholder: 'Enter business description',
  schema: z.string().max(2000, 'Must be at most 2000 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const primaryStockExchange = define({
  label: 'Primary Stock Exchange',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const featureSet = define({
  label: 'Feature Set',
  schema: z.array(z.string()),
  adapterHint: 'multi-select',
  displayFormat: 'text',
});

export const companyType = define({
  label: 'Company Type',
  schema: z.array(z.string()),
  adapterHint: 'multi-select',
  displayFormat: 'text',
});

export const titlePrefix = define({
  label: 'Title Prefix',
  placeholder: 'Enter title prefix',
  schema: z.string().max(50, 'Must be at most 50 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});
